type RoomView = import('../../../packages/protocol/src/messages.ts').RoomView;
type TextureAssetManifest = import('./render/kmodel.ts').TextureAssetManifest;

let textureManifest: TextureAssetManifest = { files: {} };
let textureManifestChanged: (manifest: TextureAssetManifest) => void = () => {};
let refreshTextureDelivery: (force?: boolean) => Promise<void> = async () => {};
function connectTextureDelivery(loader: typeof import('./render/kmodel.ts')): void {
  textureManifestChanged = loader.setTextureAssetManifest;
  textureManifestChanged(textureManifest);
  loader.setTextureAssetRefresh(refreshTextureDelivery);
}

let prefetchAssets: (room?: RoomView) => void = () => {};

/** Version existing loaders at the delivery boundary. This also covers HTTP LAN
 * phones, where service workers are unavailable. */
const development = (import.meta as ImportMeta & { env: { DEV: boolean } }).env.DEV || location.port === '5173';
async function assetDelivery(): Promise<void> {
  if (development) return;
  const network = window.fetch.bind(window);
  type Manifest = { id: string; files: Record<string, string> };
  let manifest: Manifest | undefined;
  let verifiedManifest: Manifest | undefined;
  let checking: Promise<void> | undefined;
  let checkedAt = 0;
  const valid = (value: Manifest): boolean => /^[a-f0-9]{64}$/.test(value?.id ?? '') &&
    !!value.files && typeof value.files === 'object';
  const installManifest = (next?: Manifest): void => {
    manifest = next;
    if (next) verifiedManifest = next;
    textureManifest = next ?? { files: {} };
    textureManifestChanged(textureManifest);
  };
  const logical = (source: string): URL | undefined => {
    try {
      const url = new URL(source, document.baseURI);
      if (url.origin === location.origin && url.pathname.startsWith('/assets/') &&
        !['/assets/build-id', '/assets/manifest'].includes(url.pathname) &&
        !['hash', 'revision', 'sha256', 'build'].some(key => url.searchParams.has(key))) return url;
    } catch { /* Leave malformed and external URLs to the native loader. */ }
    return undefined;
  };
  const workerManifest = async (): Promise<Manifest | undefined> => {
    const worker = navigator.serviceWorker?.controller;
    if (!worker) return;
    // A busy rendering thread can time out its own metadata fetch while the worker
    // remains healthy. Ask it to validate the build before bypassing its warm cache.
    return new Promise(resolve => {
      const channel = new MessageChannel();
      const finish = (next?: Manifest) => {
        clearTimeout(timer); channel.port1.close(); channel.port2.close?.(); resolve(next);
      };
      const timer = setTimeout(() => finish(), 1500);
      channel.port1.onmessage = event => {
        const next = event.data?.manifest as Manifest;
        finish(valid(next) && event.data.id === next.id ? next : undefined);
      };
      channel.port1.onmessageerror = () => finish();
      try { worker.postMessage({ type: 'assets-refresh' }, [channel.port2]); } catch { finish(); }
    });
  };
  // Coalesce sequential loader sub-batches too. Each race boundary forces a
  // check, and errors force another; ordinary assets share five seconds of proof.
  // A failed check is never retained as permission to serve stale bytes.
  const refresh = (force = false): Promise<void> => {
    if (checking) return checking;
    if (!force && manifest && Date.now() - checkedAt < 5000) return Promise.resolve();
    checking = (async () => {
      try {
        const response = await network('/assets/build-id', { cache: 'no-cache', signal: AbortSignal.timeout(5000) });
        if (!response.ok) throw new Error('Asset build unavailable');
        const build = await response.json() as { id: string };
        // Recover an outage only after the server validates the previous build;
        // it must not force a megabyte manifest download on every warm race.
        if (!manifest && verifiedManifest?.id === build.id) installManifest(verifiedManifest);
        if (build.id !== manifest?.id) {
          const response = await network('/assets/manifest', { cache: 'no-cache', signal: AbortSignal.timeout(5000) });
          if (!response.ok) throw new Error('Asset manifest unavailable');
          const next = await response.json() as Manifest;
          if (!valid(next)) throw new Error('Invalid asset manifest');
          installManifest(next);
          navigator.serviceWorker?.controller?.postMessage({ type: 'assets-refresh' });
        }
        checkedAt = Date.now();
      } catch {
        let next: Manifest | undefined;
        try { next = await workerManifest(); } catch { /* No worker/private mode: ordinary networking. */ }
        installManifest(next);
      }
    })().finally(() => { checking = undefined; });
    return checking;
  };
  refreshTextureDelivery = refresh;
  const version = (source: string): string => {
    const url = logical(source);
    const hash = url && manifest?.files[url.pathname];
    if (!url) return source;
    if (!/^[a-f0-9]{64}$/.test(hash ?? '')) {
      // Native images have no Request.cache option, and devtools/routing can
      // override that mode for fetch. An explicit bypass reaches the worker in
      // both cases; an outage/removed asset cannot reuse its old hash mapping.
      url.searchParams.set('asset-network', '1'); return url.href;
    }
    url.searchParams.set(window.isSecureContext ? 'hash' : 'revision', hash!);
    return url.href;
  };
  if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', event => {
    // A worker refresh also updates every already-open page, not just its cache.
    if (event.data?.type === 'assets-manifest' && valid(event.data.manifest)) installManifest(event.data.manifest);
  });
  try {
    // Cold hashing must not hold up the title/audio gesture handlers or the 8 s
    // boot guard. The worker can finish discovering a cold build independently.
    const response = await network('/assets/manifest', { cache: 'no-cache', signal: AbortSignal.timeout(5000) });
    if (response.ok) {
      const next = await response.json() as Manifest;
      if (valid(next)) installManifest(next);
    }
  } catch { /* Missing metadata/offline servers use ordinary asset networking. */ }
  let planned = '', generation = 0;
  prefetchAssets = (room?: RoomView) => {
    const worker = navigator.serviceWorker?.controller;
    if (!worker || !manifest || (room && room.phase !== 'lobby')) return;
    const key = `${manifest.id}|${JSON.stringify(room?.settings)}|${JSON.stringify(room?.players.map(p => p.loadout))}`;
    if (planned === key) return;
    planned = key;
    const run = ++generation;
    void (async () => {
      const [{ courseDependencyPlan, loadObjectIndex }, { loadRoster, loadoutDependencyPlan }, { itemDependencyPlan },
        { textureTier }, { CUPS }, { DEFAULT_LOADOUT, validateLoadout }] = await Promise.all([
        import('./game/course-scene.ts'), import('./game/kart-model.ts'), import('./game/items-view.ts'),
        import('./render/kmodel.ts'), import('../../../packages/sim/src/cups.ts'), import('../../../packages/protocol/src/messages.ts'),
      ]);
      const [index, roster] = await Promise.all([loadObjectIndex(), loadRoster()]);
      await refresh(true);
      if (!manifest || run !== generation) return;
      const snapshot = manifest, tier = textureTier();
      const urls = new Set<string>();
      let saved = DEFAULT_LOADOUT;
      try { saved = validateLoadout(JSON.parse(localStorage.getItem('kart.ui.loadout') ?? 'null')) ?? saved; } catch { /* default loadout */ }
      const loadouts = room ? room.players.filter(p => !p.spectating).map(p => p.loadout) : [saved];
      if (roster) for (const loadout of loadouts) for (const url of loadoutDependencyPlan(loadout, roster, snapshot, tier).urls) urls.add(url);
      if (room) {
        const courseId = room.settings.mode === 'gp'
          ? CUPS.find(c => c.id === room.settings.cupId)?.courses[room.gp?.raceIndex ?? 0]
          : room.settings.courseId;
        // Random courses cannot be known until raceLoad; don't download every course speculatively.
        if (courseId && courseId !== 'random') {
          const response = await fetch(`/assets/courses/${courseId}/course.json`);
          if (response.ok) for (const url of courseDependencyPlan(courseId, await response.json(), index, snapshot, tier).urls) urls.add(url);
        }
        for (const url of itemDependencyPlan(index, snapshot, tier).urls) urls.add(url);
      }
      if (run !== generation || manifest?.id !== snapshot.id) return;
      worker.postMessage({ type: 'assets-prefetch', id: snapshot.id,
        urls: [...urls].filter(url => snapshot.files[url]).map(url => new URL(`${url}?hash=${snapshot.files[url]}`, location.origin).href) });
    })().catch(() => { if (run === generation) planned = ''; });
  };
  window.fetch = async (input, init) => {
    const source = input instanceof Request ? input.url : String(input);
    if ((init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase() === 'GET' && logical(source)) {
      await refresh();
      const target = version(source);
      const send = (url: string) => network(input instanceof Request ? new Request(url, input) : url,
        new URL(url, document.baseURI).searchParams.get('asset-network') === '1' ? { ...init, cache: 'no-store' } : init);
      const response = await send(target);
      // Extraction may complete between the build check and this request.
      // Retry only automatically versioned URLs, never caller-owned hashes.
      if (target !== source && response.status === 404) {
        await refresh(true);
        const retry = version(source);
        if (retry !== target) return send(retry);
      }
      return response;
    }
    return network(input, init);
  };
  // Both native Image and Three.ImageLoader assign .src. Preserve the
  // logical URL read by colour-variant/material code while the actual src
  // attribute/network URL uses the hash. A later setAttribute is detected
  // by comparing the native getter, so it cannot retain an obsolete alias.
  const descriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
  if (descriptor?.get && descriptor.set && descriptor.configurable) {
    const aliases = new WeakMap<HTMLImageElement, { source: string; target: string; cancel?: () => void }>();
    const get = descriptor.get, set = descriptor.set;
    Object.defineProperty(HTMLImageElement.prototype, 'src', { ...descriptor,
      get(this: HTMLImageElement) {
        const target = get.call(this) as string;
        const alias = aliases.get(this);
        return alias?.target === target ? alias.source : target;
      },
      set(this: HTMLImageElement, source: string) {
        source = String(source);
        aliases.get(this)?.cancel?.();
        if (!logical(source)) { aliases.delete(this); set.call(this, source); return; }
        const alias: { source: string; target: string; cancel?: () => void } = {
          source: new URL(source, document.baseURI).href, target: get.call(this) as string,
        };
        aliases.set(this, alias);
        // Refresh BEFORE assigning src: cached old images return 200, so an
        // onerror-only retry cannot fix a tab left open across a rebuild.
        void refresh().then(async () => {
          if (aliases.get(this) !== alias || get.call(this) !== alias.target) return;
          const target = version(source);
          // Native Image bypasses the shared fetch transport. Decode its exact
          // verified bytes through a temporary object URL so concurrent menu
          // icons and legacy model images can use demand aggregates too.
          let imageTarget = target, release = () => {};
          if (textureManifest.aggregateEndpoint && /\.webp$/.test(new URL(source, document.baseURI).pathname) &&
            !new URL(target).searchParams.has('asset-network')) {
            try {
              const response = await window.fetch(target);
              if (response.ok) {
                imageTarget = URL.createObjectURL(await response.blob());
                release = () => { URL.revokeObjectURL(imageTarget); };
              }
            } catch { /* The ordinary Image request remains the fallback. */ }
          }
          if (aliases.get(this) !== alias || get.call(this) !== alias.target) { release(); return; }
          // As with fetch, extraction can finish after the check but before an
          // uncached image arrives. Delay the loader's error until one refresh;
          // genuine missing/invalid images still report their original failure.
          const failed = (event: Event) => {
            if (aliases.get(this) !== alias) return;
            event.stopImmediatePropagation(); alias.cancel?.();
            void refresh(true).then(() => {
              if (aliases.get(this) !== alias || get.call(this) !== alias.target) return;
              const retry = version(source);
              if (retry === target) this.dispatchEvent(new Event('error'));
              else { set.call(this, retry); alias.target = get.call(this) as string; }
            });
          };
          this.addEventListener('error', failed, true);
          const loaded = () => release();
          this.addEventListener('load', loaded, { once: true });
          alias.cancel = () => { this.removeEventListener('error', failed, true); this.removeEventListener('load', loaded); release(); };
          set.call(this, imageTarget);
          alias.target = get.call(this) as string;
        });
      },
    });
  }
}

// Registration is independent of input/audio startup: no gesture handlers or
// waiting for a controller, and no cached navigation shell on iPhone PWAs.
if (!development && window.isSecureContext && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).then(() => {
    navigator.serviceWorker.controller?.postMessage({ type: 'assets-refresh' });
  }).catch(() => { /* HTTP LAN, private browsing and unavailable SWs still work. */ });
}
await assetDelivery();

const params = new URLSearchParams(location.search);
connectTextureDelivery(await import('./render/kmodel.ts'));
const view = params.get('view');
const app = document.getElementById('app')!;
if (!params.has('check')) void import('./game/bug-report.ts').then((m) => m.installBugReport());

if (view === 'ui-preview') {
  const { startUiPreview } = await import('./dev/ui-preview.ts');
  await startUiPreview(app);
} else if (view === 'course') {
  const { startCourseViewer } = await import('./dev/course-viewer.ts');
  await startCourseViewer(app, params.get('id') ?? 'Gu_FirstCircuit');
} else if (view === 'particles') {
  const { startParticleViewer } = await import('./dev/particle-viewer.ts');
  await startParticleViewer(app);
} else if (view === 'drive') {
  const { startDrive } = await import('./dev/drive.ts');
  await startDrive(app, params.get('id') ?? 'Gu_FirstCircuit');
} else if (view === 'localrace') {
  const { startLocalRace } = await import('./dev/local-race.ts');
  await startLocalRace(app, params.get('id') ?? 'Gu_FirstCircuit');
} else if (view === 'roster') {
  const m = await import('./dev/roster-viewer.ts');
  await (params.get('gallery') ? m.startRosterGallery(app) : m.startRosterViewer(app));
} else if (view === 'modelqa') { await (await import('./dev/model-qa.ts')).startModelQa(app);
} else if (view === 'replay') { const { startReplayView } = await import('./dev/replay-view.ts'); await startReplayView(app);
} else if (view === 'simdebug') {
  const { startSimDebug } = await import('./dev/sim-debug.ts');
  await startSimDebug(app, params.get('id') ?? 'Gu_FirstCircuit');
} else {
  const { GameApp } = await import('./game/app.ts');
  const game = new GameApp(app);
  game.net.on('room', ({ room }) => prefetchAssets(room));
  navigator.serviceWorker?.addEventListener('controllerchange', () => prefetchAssets(game.net.latestRoom));
  prefetchAssets(game.net.latestRoom);
}
// The boot guard in index.html stops reporting errors once startup has succeeded.
(window as { __bootOk?: boolean }).__bootOk = true;
export {};
